import React, { useState, useEffect } from 'react';
import { COLORES_POR_DEFECTO, aplicarBranding, limpiarBranding } from '../lib/branding';
import SelectorDeColores from './business/SelectorDeColores';
import SelectorDeImagen from './business/SelectorDeImagen';
import VistaPreviaNegocio from './VistaPreviaNegocio';
import './BrandPreview.css';

const BrandPreview = ({
  isOpen,
  onClose,
  businessInfo,
  services,
  // Personal, lugares y horarios: la vista previa es la página pública de
  // verdad, así que arma los pasos de la reserva con esto. La réplica que había
  // antes no los usaba —dibujaba un calendario de adorno—, y por eso no estaban
  // en las props.
  staff = [],
  resources = [],
  schedule = {},
  branding,
  onBrandingChange,
  // La imagen del negocio va aparte del branding y no adentro: en Firestore
  // vive en la raíz del documento (`logo`), que es de donde la lee la página
  // pública y de donde la toma la imagen que se ve al compartir el link.
  // Metiéndola en `branding` habría que tocar esos tres lugares para que la
  // encuentren.
  logo,
  onLogoChange,
  currentUser,
  apiUrl,
}) => {
  /* El color de arranque de un negocio que todavía no eligió ninguno.
     Era una paleta azul escrita acá (#1976D2), distinta de la que usa el
     onboarding: el mismo negocio veía su página azul o naranja según por dónde
     entrara. Sale de lib/branding.js, que es de donde la toman el onboarding y
     la propia página pública. */
  const defaultBranding = COLORES_POR_DEFECTO;

  // Estado para la vista previa (lo que se ve, puede cambiar con paletas rápidas)
  const [localBranding, setLocalBranding] = useState({
    ...defaultBranding,
    ...branding
  });

  // Estado para los colores realmente aplicados/guardados (solo cambia con "Aplicar")
  const [appliedBranding, setAppliedBranding] = useState({
    ...defaultBranding,
    ...branding
  });

  /**
   * La imagen que se está viendo en la vista previa.
   *
   * Va en su propio estado por lo mismo que los colores: se sube y se ve al
   * instante en la página de al lado, pero recién se guarda al tocar Aplicar.
   * `logoAplicado` es a lo que vuelve el botón Restaurar.
   *
   * Ojo: el archivo YA está en Storage apenas se elige —eso no se puede
   * deshacer desde acá—. Lo que Aplicar decide es si el negocio pasa a
   * apuntar a esa imagen o se queda con la que tenía.
   */
  const [localLogo, setLocalLogo] = useState(logo || null);
  const [logoAplicado, setLogoAplicado] = useState(logo || null);

  // Actualizar ambos estados cuando cambie el prop
  useEffect(() => {
    const newBranding = {
      ...defaultBranding,
      ...branding
    };
    setLocalBranding(newBranding);
    setAppliedBranding(newBranding);
  }, [branding]);

  useEffect(() => {
    setLocalLogo(logo || null);
    setLogoAplicado(logo || null);
  }, [logo]);

  /* Acá vivían `updateColor` y `applyPreset`, que alimentaban los selectores
     sueltos y las "paletas rápidas" de este modal. Los reemplazó
     SelectorDeColores, que escribe el branding entero de una. */

  // El botón "Aplicar" pinta lo mismo que la vista previa, con la misma función.
  const applyColorsToDOM = (branding) => aplicarBranding(branding || {});

  // Aplicar colores dinámicamente al preview
  //
  // Ojo con este efecto: escribe los tokens de marca sobre <html>, o sea que
  // mientras el modal está abierto el color elegido vale para TODA la app. Eso
  // es a propósito (el preview usa CSS que cuelga del root), pero hay que
  // deshacerlo sí o sí al cerrar y al desmontar: si no, el negocio elige su
  // color en el paso 8 del onboarding y la landing —que consume los mismos
  // tokens— queda pintada con el color de ese negocio para el resto de la
  // sesión. La limpieza va por `limpiarBranding` de lib/branding.js, que conoce
  // la lista completa de propiedades; la lista escrita a mano que había acá se
  // olvidaba de --primary-color, --secondary-color y --accent-color, y encima
  // no corría nunca al desmontar.
  useEffect(() => {
    if (isOpen) {
      // Una sola llamada a `aplicarBranding`, el mismo que corre en el panel y en
      // la página pública. Acá había una tercera copia escrita a mano —la de las
      // once líneas de setProperty, duplicadas para el contenedor y para el
      // root— que se había quedado atrás de las otras dos: no escribía los
      // tokens de shadcn ni la paleta, así que la vista previa mostraba menos
      // color del que en realidad se iba a aplicar al aceptar. Elegir mirando
      // una vista previa que miente es peor que no tenerla.
      aplicarBranding(localBranding);

      // Se limpia al cerrar, al cambiar de color (se vuelve a aplicar en la
      // misma pasada, así que no parpadea) y —lo que faltaba— al desmontarse el
      // modal. También deshace lo que escribió el botón "Aplicar", que llama a
      // applyColorsToDOM y después cierra.
      return () => limpiarBranding();
    }

    // Cerrado: no se toca nada. Limpiar acá borraría los tokens que el
    // Dashboard escribe por su cuenta al cargar, sin que el modal se haya
    // abierto nunca.
    return undefined;
  }, [localBranding, isOpen]);

  if (!isOpen) return null;

  /* Acá vivían `mockBusinessData` —con servicios de ejemplo escritos a mano,
     incluido uno llamado "Corte De Caca" que llegó a producción— y
     `generateStaticCalendar`, que armaba 30 días de adorno. Los dos alimentaban
     la réplica del BusinessPage. La vista previa ahora monta la página real con
     los datos reales del negocio, así que no hay nada que simular. */

  /**
   * Lo que consume la vista previa. Es la misma forma que arma el onboarding
   * (`datosParaPreview` en Onboarding.jsx), porque del otro lado hay el mismo
   * componente.
   *
   * El branding que entra es `localBranding` y no `branding`: es el que va
   * cambiando mientras se tocan los selectores, y es todo el punto de esta
   * pantalla. `appliedBranding` es el que se guarda al tocar Aplicar.
   */
  const datosDeLaVistaPrevia = {
    businessInfo,
    // La imagen va suelta y no metida adentro de `businessInfo`: acá
    // `businessInfo` es el formulario de datos del negocio que mantiene el
    // panel, y ensuciarlo con una clave que no le corresponde es lo que hacía
    // que la vista previa dependiera de un detalle de cómo la página aplana ese
    // objeto. Va la local y no la guardada: el punto de esta pantalla es verla
    // puesta antes de decidir.
    logo: localLogo,
    services,
    staff,
    resources,
    schedule,
    branding: localBranding,
  };

  return (
    <div className="brand-preview-overlay">
      <div className="brand-preview-container">
        {/* Controls Panel */}
        <div className="brand-controls-panel">
          <div className="controls-header">
            <h3>Colores e imagen de tu página</h3>
            <button className="close-preview-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="controls-content">
            {/* El mismo selector que el paso de marca del onboarding.

                Acá había una interfaz propia: seis "paletas rápidas" con otros
                colores, tres selectores de color con su campo hexadecimal al
                lado, y un modo "Avanzado" que los escondía en el teléfono. O sea
                que el mismo negocio elegía su color de dos maneras distintas
                según entrara por el onboarding o por el panel, y con dos listas
                de colores que no coincidían: la de acá arrancaba en azul y la de
                allá arranca en el naranja de Aturno.

                El de allá además da la vuelta al círculo cromático con 18 tonos
                y arma el secundario y el de acento a partir del principal, así
                que el degradado del encabezado se lee como una familia. Elegir
                los tres a mano —que es lo que había acá— permite combinaciones
                que dejan el encabezado ilegible. */}
            <SelectorDeColores value={localBranding} onChange={setLocalBranding} />

            {/* La imagen, abajo del color y adentro del mismo modal: las dos
                cosas son "cómo se ve mi página", se miran contra la misma vista
                previa de la derecha y se guardan con el mismo botón. En una
                pantalla aparte habría que elegir un color, aplicar, entrar a
                otro lado y recién ahí ver la foto sobre ese color. */}
            <div className="control-group">
              <label className="control-label">Imagen de tu negocio</label>
              <SelectorDeImagen
                value={localLogo}
                onChange={setLocalLogo}
                uid={currentUser?.uid}
                nombreDelNegocio={businessInfo?.name}
              />
            </div>

            <div className="control-actions">
              {/* El botón "Avanzado" se fue con los controles que escondía: eran
                  los tres selectores de color sueltos, que ya no existen. */}
              <button
                className="btn-apply-changes"
                onClick={async () => {
                  try {
                    console.log('🔧 DEBUG: Iniciando aplicación de cambios');
                    console.log('🔧 DEBUG: currentUser:', currentUser);
                    console.log('🔧 DEBUG: apiUrl:', apiUrl);
                    console.log('🔧 DEBUG: localBranding:', localBranding);
                    
                    // GUARDAR la vista previa como "aplicada"
                    setAppliedBranding(localBranding);
                    setLogoAplicado(localLogo);
                    console.log('✅ Colores guardados como aplicados:', localBranding);
                    
                    // Aplicar colores globalmente al documento inmediatamente
                    applyColorsToDOM(localBranding);
                    
                    // Guardar en la base de datos
                    if (currentUser && apiUrl) {
                      console.log('🔧 DEBUG: currentUser y apiUrl están disponibles');
                      console.log('🔧 DEBUG: Obteniendo token...');
                      const token = await currentUser.getIdToken();
                      console.log('🔧 DEBUG: Token obtenido:', token ? 'SÍ' : 'NO');
                      console.log('🔧 DEBUG: URL completa:', `${apiUrl}/api/business/branding`);
                      console.log('🔧 DEBUG: Datos a enviar:', localBranding);
                      
                      const response = await fetch(`${apiUrl}/api/business/branding`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        // `logo` va explícito y puede ser null: null es "sacala",
                        // que es distinto de no mandarla. El backend sólo toca
                        // la imagen si la clave viene en el cuerpo.
                        body: JSON.stringify({ ...localBranding, logo: localLogo })
                      });
                      
                      console.log('🔧 DEBUG: Respuesta recibida:', {
                        status: response.status,
                        statusText: response.statusText,
                        ok: response.ok,
                        url: response.url
                      });
                      
                      if (response.ok) {
                        console.log('✅ Colores guardados en la base de datos exitosamente');
                        
                        // También guardar en localStorage como backup
                        localStorage.setItem('aturno_branding', JSON.stringify(localBranding));
                        
                        // Actualizar el estado del Dashboard
                        onBrandingChange(localBranding);
                        // La imagen NO va al localStorage de arriba: eso es un
                        // respaldo de colores para pintar rápido al abrir el
                        // panel, y una URL de imagen guardada ahí sobreviviría
                        // a sacarla desde otro dispositivo.
                        if (onLogoChange) onLogoChange(localLogo);
                      } else {
                        throw new Error('Error al guardar en la base de datos');
                      }
                    } else {
                      // Fallback si no hay usuario autenticado
                      console.log('🔧 DEBUG: currentUser o apiUrl no disponibles');
                      console.log('🔧 DEBUG: currentUser disponible:', !!currentUser);
                      console.log('🔧 DEBUG: apiUrl disponible:', !!apiUrl);
                      localStorage.setItem('aturno_branding', JSON.stringify(localBranding));
                      onBrandingChange(localBranding);
                    }
                    
                    console.log('✅ Colores aplicados globalmente:', localBranding);
                    onClose();
                    
                  } catch (error) {
                    console.error('❌ Error guardando colores:', error);
                    console.log('🔧 DEBUG: Tipo de error:', typeof error);
                    console.log('🔧 DEBUG: Error completo:', {
                      message: error.message,
                      name: error.name,
                      stack: error.stack
                    });
                    
                    // Fallback: guardar en localStorage
                    localStorage.setItem('aturno_branding', JSON.stringify(localBranding));
                    onBrandingChange(localBranding);
                    onClose();
                  }
                }}
              >
                ✓ Aplicar
              </button>
              <button 
                className="btn-reset-colors"
                onClick={() => {
                  console.log('🔄 Restaurando a última configuración aplicada:', appliedBranding);
                  
                  // Restaurar vista previa a la última configuración aplicada
                  setLocalBranding(appliedBranding);
                  setLocalLogo(logoAplicado);
                  
                  // Aplicar colores inmediatamente al DOM
                  applyColorsToDOM(appliedBranding);
                }}
              >
                ↺ Restaurar
              </button>
            </div>
          </div>
        </div>

        {/* La vista previa es el MISMO componente que usa el onboarding, que a
            su vez monta el BusinessPage de verdad con los datos sin guardar
            (components/VistaPreviaNegocio.jsx).

            Acá había una réplica de 177 líneas, rotulada por su propio autor
            como "COPIA EXACTA DEL BUSINESSPAGE SIN FUNCIONALIDAD". Copia exacta
            de la página tal como era el día que se escribió: no tenía los pasos
            numerados, ni el mensaje del negocio, ni el resumen de la reserva, ni
            los horarios de atención — todo lo que la página pública fue ganando
            desde entonces. Una réplica no se entera de esos cambios: hay que
            acordarse de copiarlos, y nadie se acuerda.

            Además arrastraba las clases compartidas (`calendar-header`,
            `calendar-day-header`, `day-number`, `booking-calendar`), o sea que
            la maquetaban a la vez BookingCalendar.css, BusinessPage.css y
            Home.css. */}
        <div className="brand-preview-page">
          <VistaPreviaNegocio datos={datosDeLaVistaPrevia} completa />
        </div>
      </div>
    </div>
  );
};

export default BrandPreview; 